import { useTranslation } from "react-i18next";
import { IFileItem } from "./types";
import { useEffect, useState } from "react";
import { endpointService } from "#src/services/endpoint";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import { TableV2 } from "#src/commonV2/Table";
import { IDeviceDriveFileSnapshot } from "#src/services/endpoint/devices/types";
import { DateTime } from "luxon";
import { ButtonV2 } from "#src/commonV2/Button";
import { createToast } from "#src/common/system/toasts";
import { byteToMegabyte } from "#src/utils/common";
import { ModalV2 } from "#src/commonV2/modal/ModalV2";

interface IEndpointDeviceManageSnapshotModalProps {
  workspaceId: string;
  deviceId: string;
  fileId: string | undefined;
  open: boolean;
  onClose: () => void;
  selectedFileItem: IFileItem | null;
}

export const EndpointDeviceManageSnapshotModal = ({
  workspaceId,
  deviceId,
  fileId,
  open,
  onClose,
  selectedFileItem,
}: IEndpointDeviceManageSnapshotModalProps) => {
  const { t } = useTranslation("endpoint", {
    keyPrefix: "devices.drive.modal.restore",
  });

  // --------------- STATES ---------------

  const [snapshotData, setSnapshotData] =
    useState<IDeviceDriveFileSnapshot | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);

  // --------------- METHODS ---------------

  const onCloseAndCleanup = () => {
    setSnapshotData(null);
    onClose();
  };

  const restoreSnapshot = (
    snapshot: IDeviceDriveFileSnapshot["file_versions"][number]
  ) => {
    if (fileId) {
      endpointService
        .restoreFileSnapshot(workspaceId, deviceId, fileId, snapshot.version_id)
        .then((response) => {
          console.log(response);
          createToast({
            type: "success",
            message: t("notification.restoreSuccess.message"),
            detail: t("notification.restoreSuccess.detail"),
          });
          onCloseAndCleanup();
          // Might want to call onRefresh, but currently we don't need to.
        })
        .catch(apiErrorHandler);
    }
  };

  // --------------- EFFECTS ---------------

  useEffect(() => {
    if (workspaceId && deviceId && selectedFileItem && selectedFileItem.id) {
      setLoading(true);
      endpointService
        .listFileSnapshots(workspaceId, deviceId, selectedFileItem.id)
        .then((response) => {
          setSnapshotData(response);
          setLoading(false);
        })
        .catch(apiErrorHandler);
    }
  }, [workspaceId, deviceId, selectedFileItem]);

  // --------------- RENDER ---------------

  console.log(snapshotData, selectedFileItem);

  return (
    <ModalV2
      open={open}
      onChangeOpen={() => {
        onCloseAndCleanup();
      }}
      className="w-[37.5rem]"
    >
      <span className="text-lg font-semibold text-gray-v2-900">
        {t("title")}
      </span>
      <span className="text-md text-gray-v2-600">
        {t("hint")}
        <span className="font-semibold">
          {selectedFileItem ? selectedFileItem.name : null}
        </span>
      </span>
      <TableV2<IDeviceDriveFileSnapshot["file_versions"][number]>
        data={snapshotData ? snapshotData.file_versions : []}
        isLoading={isLoading}
        getRowKey={(item) => item.version_id}
        columns={[
          {
            title: t("table.header.snapshotVersion"),
            render: (item) =>
              DateTime.fromISO(item.last_modified).toFormat(
                "HH:mm:ss dd/MM/yyyy"
              ),
            gridTemplateValue: "3fr",
          },
          {
            title: t("table.header.snapshotSize"),
            render: (item) => byteToMegabyte(item.size).toFixed(1),
            gridTemplateValue: "2fr",
          },
          {
            title: null,
            render: (item) => (
              <ButtonV2
                variant="secondary"
                size="sm"
                onClick={() => {
                  restoreSnapshot(item);
                }}
              >
                {t("table.button.restore")}
              </ButtonV2>
            ),
            gridTemplateValue: "1fr",
            overwriteWrapperProps: {
              className: "py-1 justify-end",
            },
          },
        ]}
      />
    </ModalV2>
  );
};
